import React, { useMemo, useState, useCallback } from 'react';
import EntityTable from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTable';
import { AppTableColumnDef, ExpandRowColumnLayout } from '@stackline/ui';
import { Status } from '../ShelfPage/styles';
import { Box } from '@mui/system';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import axios from 'axios';
import { buildLlmCrawlerPath } from 'src/utils/app';

type Campaign = Record<string, any>;

interface CrawlCampaignsEntityTableProps {
  campaigns: Campaign[];
  isLoading: boolean;
  lastRunByCampaignId: Record<string, string>;
  lastRunTsByCampaignId: Record<string, number>;
  onSelectCampaign: (campaign: Campaign) => void;
  batchCountByCampaignId?: Record<string, number>;
}

const getId = (c: Campaign) => c?.id || c?.campaignId || c?.crawlCampaignId || c?.uuid || c?.campaign_id || c?.crawl_campaign_id;

const NextRun: React.FC<{ arn?: string | null }> = ({ arn }) => {
  const [label, setLabel] = React.useState<string>('—');
  React.useEffect(() => {
    let active = true;
    (async () => {
      try {
        const path = buildLlmCrawlerPath(`scheduler/next-execution?arn=${String(arn)}`);
        const { data } = await axios.get(path, { params: { env: 'dev' } });
        const raw = (data && (data.nextExecution || data.nextExecutionTime || data.next || data.timestamp)) || data;
        let d: Date | null = null;
        if (typeof raw === 'number') d = new Date(raw > 1e12 ? raw : raw * 1000);
        else if (typeof raw === 'string') d = new Date(raw);
        if (active) setLabel(d ? d.toLocaleString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');
      } catch (_e) {
        if (active) setLabel('—');
      }
    })();
    return () => {
      active = false;
    };
  }, [arn]);
  return (
    <Text sx={{ fontFeatureSettings: '"liga" off, "clig" off', fontFamily: 'Roboto', fontSize: 12, fontStyle: 'normal', fontWeight: 400, lineHeight: '18px', letterSpacing: '0.08px' }}>{label}</Text>
  );
};

const CrawlCampaignsEntityTable: React.FC<CrawlCampaignsEntityTableProps> = ({
  campaigns,
  isLoading,
  lastRunByCampaignId,
  lastRunTsByCampaignId,
  onSelectCampaign,
  batchCountByCampaignId
}) => {
  const rows = useMemo(() => {
    const toDisplayCost = (value: any) => {
      if (value === null || value === undefined || value === '') return '—';
      const num = Number(value);
      if (Number.isNaN(num)) return '—';
      if (num === 0) return '—';
      return `$${num.toLocaleString()}`;
    };

    const toFrequency = (v: any) => {
      const str = (v || '').toString();
      if (!str) return '—';
      return str.charAt(0).toUpperCase() + str.slice(1);
    };

    const items = [...campaigns].map((c: Campaign) => {
      const id = getId(c);
      const name = c.name || c.campaignName || c.title || '—';
      const template = c.templateType || c.type || c.category || '—';
      const retailers = Array.isArray(c.retailers) ? c.retailers.length : c.retailerCount ?? '—';
      const frequency = toFrequency(c.frequency || c.schedule?.frequency);
      const lastRun = (id && lastRunByCampaignId[String(id)]) || '—';
      const credits = c.credits ?? '—';
      const costNum = typeof c.cost === 'number' ? c.cost : Number(String(c.cost || '').replace(/[^0-9.\-]/g, ''));
      const cost = toDisplayCost(costNum);
      const statusRaw = (c.status || '').toString().toLowerCase();
      const isDisabled = ['disabled', 'inactive'].includes(statusRaw);
      const ts = lastRunTsByCampaignId[String(id)] ?? -Infinity;
      return {
        __raw: c,
        id,
        name,
        template,
        retailers,
        frequency,
        lastRun,
        credits,
        cost,
        costNum: Number.isFinite(costNum) ? costNum : -Infinity,
        creditsNum: typeof c.credits === 'number' ? c.credits : Number(String(c.credits ?? '').replace(/,/g, '')) || -Infinity,
        status: isDisabled ? 'disabled' : 'enabled',
        __ts: ts
      };
    });

    return items.sort((a, b) => (b.__ts as number) - (a.__ts as number));
  }, [campaigns, lastRunByCampaignId, lastRunTsByCampaignId]);

  // Sorting state and utils
  const [sortField, setSortField] = useState<keyof typeof rows[number] | '__ts'>('__ts');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const toggleSort = useCallback((field: keyof typeof rows[number] | '__ts') => {
    setSortField((prev) => {
      if (prev === field) {
        setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortDirection('asc');
      return field;
    });
  }, []);

  const arrowFor = (_field: string) => null;

  const renderSortableHeader = (label: string, field: keyof typeof rows[number] | '__ts') => (
    <span
      onClick={() => toggleSort(field)}
      style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, verticalAlign: 'middle' }}
      title={`Sort by ${label}`}
    >
      <span style={{ fontSize: 12, fontWeight: 700, lineHeight: '20px' }}>{label}</span>
      {/* sorting arrow removed by design */}
    </span>
  );

  const sortedRows = useMemo(() => {
    const arr = [...rows];
    const dir = sortDirection === 'asc' ? 1 : -1;
    const get = (r: any, f: any) => r[f];
    const compare = (a: any, b: any) => {
      const av = get(a, sortField);
      const bv = get(b, sortField);
      const an = typeof av === 'number' ? av : Number(av);
      const bn = typeof bv === 'number' ? bv : Number(bv);
      // Prefer numeric when both numeric
      if (!Number.isNaN(an) && !Number.isNaN(bn)) return (an - bn) * dir;
      const as = (av ?? '').toString().toLowerCase();
      const bs = (bv ?? '').toString().toLowerCase();
      if (as < bs) return -1 * dir;
      if (as > bs) return 1 * dir;
      return 0;
    };
    return arr.sort(compare);
  }, [rows, sortField, sortDirection]);

  const columnDefs: (Omit<AppTableColumnDef, 'field'> & Pick<Partial<AppTableColumnDef>, 'field'>)[] = [
    {
      headerName: renderSortableHeader('Agent Name', 'name' as any) as any,
      field: 'name',
      tableColumnProps: { style: { width: 'auto', minWidth: '220px', paddingRight: '16px' } },
      valueFormatter: (value: string, row: any) => {
        const label = value || '—';
        const maxChars = 42;
        const show = label.length > maxChars ? label.slice(0, maxChars) + '…' : label;
        return (
          <div
            title={label}
            style={{ maxWidth: 420, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            aria-label={row?.id != null ? `campaign-id-${String(row.id)}` : undefined}
          >
            <Text
              variant="body2"
              sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
              onClick={() => { onSelectCampaign(row?.__raw || row); }}
            >
              {show}
            </Text>
          </div>
        );
      }
    },
    {
      headerName: renderSortableHeader('Template', 'template' as any) as any,
      field: 'template',
      tableColumnProps: { style: { width: '160px', padding: '0px' } },
      valueFormatter: (value: string) => {
        const label = value || '—';
        return (
          <div title={label} style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            <Text variant="body2">{label}</Text>
          </div>
        );
      }
    },
    {
      headerName: renderSortableHeader('Retailers', 'retailers' as any) as any,
      field: 'retailers',
      tableColumnProps: { style: { width: '120px', padding: '0px' } }
    },
    {
      headerName: renderSortableHeader('Frequency', 'frequency' as any) as any,
      field: 'frequency',
      tableColumnProps: { style: { width: '140px', padding: '0px' } },
      valueFormatter: (value: string) => {
        const label = value || '—';
        return (
          <div title={label} style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            <Text variant="body2">{label}</Text>
          </div>
        );
      }
    },
    {
      headerName: renderSortableHeader('Last Run', '__ts' as any) as any,
      field: 'lastRun',
      tableColumnProps: { style: { width: '140px', padding: '0px' } },
      valueFormatter: (value: string) => {
        const label = value || '—';
        return (
          <div title={label} style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            <Text variant="body2">{label}</Text>
          </div>
        );
      }
    },
    {
      headerName: renderSortableHeader('Credits', 'creditsNum' as any) as any,
      field: 'credits',
      tableColumnProps: { style: { width: '120px', padding: '0px' } },
      valueFormatter: (value: string) => {
        const label = String(value ?? '—');
        return (
          <div title={label} style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            <Text variant="body2">{label}</Text>
          </div>
        );
      }
    },
    {
      headerName: renderSortableHeader('Cost', 'costNum' as any) as any,
      field: 'cost',
      tableColumnProps: { style: { width: '120px', padding: '0px' } },
      valueFormatter: (value: string) => {
        const label = value || '—';
        return (
          <div title={label} style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            <Text variant="body2">{label}</Text>
          </div>
        );
      }
    },
    {
      headerName: renderSortableHeader('Status', 'status' as any) as any,
      field: 'status',
      tableColumnProps: { style: { width: '120px', padding: '0px' } },
      valueFormatter: (_: string, row: any) => {
        const isDisabled = row.status === 'disabled';
        return (
          <Status variant={isDisabled ? 'disabled' : 'enabled'}>
            {isDisabled ? 'Disabled' : 'Enabled'}
          </Status>
        );
      }
    },
    // Expand caret column
    {
      field: '',
      headerName: '',
      tableColumnProps: { style: {} },
      expandRow: true as any
    }
  ];

  return (
    <EntityTable
      isLoading={isLoading}
      rowData={sortedRows.map((r) => ({ ...r, __batchCounts: (typeof (batchCountByCampaignId) === 'object' ? batchCountByCampaignId : {}) }))}
      columnDefs={columnDefs}
      shouldModifyColumnDefs={false}
      onRowClick={undefined as unknown as () => any}
      expandRowDef={{
        containerClassName: '',
        // Render custom details content from campaign model
        valueFormatter: (row: any) => {
          const c = row?.__raw || {};
          const name = (c?.name || row?.name || '—') as string;
          const templateRaw = c?.templateType || row?.template || '—';
          const template = typeof templateRaw === 'string' ? templateRaw.charAt(0).toUpperCase() + templateRaw.slice(1) : templateRaw;
          const retailerCount = (typeof c?.retailerCount === 'number' ? c.retailerCount : (typeof row?.retailers === 'number' ? row.retailers : '—')) as any;
          const statusRaw = (c?.status || row?.status || '').toString().toLowerCase();
          const statusLabel = statusRaw === 'enabled' ? 'Enabled' : statusRaw === 'disabled' ? 'Disabled' : statusRaw === 'archived' ? 'Archived' : '—';
          const startDate = c?.startTime ? new Date(c.startTime) : null;
          const endDate = c?.endTime ? new Date(c.endTime) : null;
          const startStr = startDate ? startDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '—';
          const endStr = endDate ? endDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '—';
          const rawFrequency = c?.frequency || row?.frequency || '';
          const freqStr = (() => {
            if (!rawFrequency) return '—';
            const s = String(rawFrequency);
            const lower = s.toLowerCase();
            if (['daily', 'weekly', 'monthly'].includes(lower)) return lower.charAt(0).toUpperCase() + lower.slice(1);
            // Likely a cron expression; display Custom with tooltip
            return (
              <Text variant="body2" title={s}>Custom</Text>
            ) as any;
          })();
          const contextCount = typeof c?.crawlCampaignContextCount === 'number' ? c.crawlCampaignContextCount : '—';
          const batchCount = (() => {
            try {
              const id = getId(c);
              return (row?.__batchCounts && typeof row.__batchCounts[id] === 'number') ? row.__batchCounts[id] : '—';
            } catch {
              return '—';
            }
          })();
          const eventScheduleArn = (c?.eventBridgeScheduleArn || null) as (string | null);

          return (
            <Box height="auto" paddingX="24px" paddingY="16px">
              <Flex flexDirection="column" gap="md">
                <Text sx={{ fontFeatureSettings: '"liga" off, "clig" off', fontFamily: 'Roboto', fontSize: 12, fontStyle: 'normal', fontWeight: 500, lineHeight: '18px', letterSpacing: '0.08px' }}>Agent Details</Text>
                <Flex>
                  <Flex width="50%" gap="sm" flexDirection="column" style={{ minWidth: 360 }}>
                    <Flex gap="sm">
                      <Text sx={{ minWidth: 120, fontFeatureSettings: '"liga" off, "clig" off', fontFamily: 'Roboto', fontSize: 12, fontStyle: 'normal', fontWeight: 400, lineHeight: '18px', letterSpacing: '0.08px' }}>Name:</Text>
                      <div style={{ overflowWrap: 'anywhere' }}>
                        <Text sx={{ fontFeatureSettings: '"liga" off, "clig" off', fontFamily: 'Roboto', fontSize: 12, fontStyle: 'normal', fontWeight: 400, lineHeight: '18px', letterSpacing: '0.08px' }}>{name}</Text>
                      </div>
                    </Flex>
                    <Flex gap="sm">
                      <Text sx={{ minWidth: 120, fontFeatureSettings: '"liga" off, "clig" off', fontFamily: 'Roboto', fontSize: 12, fontStyle: 'normal', fontWeight: 400, lineHeight: '18px', letterSpacing: '0.08px' }}>Template:</Text>
                      <Text sx={{ fontFeatureSettings: '"liga" off, "clig" off', fontFamily: 'Roboto', fontSize: 12, fontStyle: 'normal', fontWeight: 400, lineHeight: '18px', letterSpacing: '0.08px' }}>{template}</Text>
                    </Flex>
                    <Flex gap="sm">
                      <Text sx={{ minWidth: 120, fontFeatureSettings: '"liga" off, "clig" off', fontFamily: 'Roboto', fontSize: 12, fontStyle: 'normal', fontWeight: 400, lineHeight: '18px', letterSpacing: '0.08px' }}>Retailer Count:</Text>
                      <Text sx={{ fontFeatureSettings: '"liga" off, "clig" off', fontFamily: 'Roboto', fontSize: 12, fontStyle: 'normal', fontWeight: 400, lineHeight: '18px', letterSpacing: '0.08px' }}>{retailerCount}</Text>
                    </Flex>
                    <Flex gap="sm">
                      <Text sx={{ minWidth: 120, fontFeatureSettings: '"liga" off, "clig" off', fontFamily: 'Roboto', fontSize: 12, fontStyle: 'normal', fontWeight: 400, lineHeight: '18px', letterSpacing: '0.08px' }}>Status:</Text>
                      <Text sx={{ fontFeatureSettings: '"liga" off, "clig" off', fontFamily: 'Roboto', fontSize: 12, fontStyle: 'normal', fontWeight: 400, lineHeight: '18px', letterSpacing: '0.08px' }}>{statusLabel}</Text>
                    </Flex>
                    <Flex gap="sm">
                      <Text sx={{ minWidth: 120, fontFeatureSettings: '"liga" off, "clig" off', fontFamily: 'Roboto', fontSize: 12, fontStyle: 'normal', fontWeight: 400, lineHeight: '18px', letterSpacing: '0.08px' }}>Next Agent Run:</Text>
                      <NextRun arn={eventScheduleArn} />
                    </Flex>
                  </Flex>

                  <Flex width="50%" gap="sm" flexDirection="column">
                    <Flex gap="sm">
                      <Text sx={{ minWidth: 120, fontFeatureSettings: '"liga" off, "clig" off', fontFamily: 'Roboto', fontSize: 12, fontStyle: 'normal', fontWeight: 400, lineHeight: '18px', letterSpacing: '0.08px' }}>Start Date:</Text>
                      <Text sx={{ fontFeatureSettings: '"liga" off, "clig" off', fontFamily: 'Roboto', fontSize: 12, fontStyle: 'normal', fontWeight: 400, lineHeight: '18px', letterSpacing: '0.08px' }}>{startStr}</Text>
                    </Flex>
                    <Flex gap="sm">
                      <Text sx={{ minWidth: 120, fontFeatureSettings: '"liga" off, "clig" off', fontFamily: 'Roboto', fontSize: 12, fontStyle: 'normal', fontWeight: 400, lineHeight: '18px', letterSpacing: '0.08px' }}>End Date:</Text>
                      <Text sx={{ fontFeatureSettings: '"liga" off, "clig" off', fontFamily: 'Roboto', fontSize: 12, fontStyle: 'normal', fontWeight: 400, lineHeight: '18px', letterSpacing: '0.08px' }}>{endStr}</Text>
                    </Flex>
                    <Flex gap="sm">
                      <Text sx={{ minWidth: 120, fontFeatureSettings: '"liga" off, "clig" off', fontFamily: 'Roboto', fontSize: 12, fontStyle: 'normal', fontWeight: 400, lineHeight: '18px', letterSpacing: '0.08px' }}>Frequency:</Text>
                      {typeof freqStr === 'string' ? <Text sx={{ fontFeatureSettings: '"liga" off, "clig" off', fontFamily: 'Roboto', fontSize: 12, fontStyle: 'normal', fontWeight: 400, lineHeight: '18px', letterSpacing: '0.08px' }}>{freqStr}</Text> : (freqStr as any)}
                    </Flex>
                    <Flex gap="sm">
                      <Text sx={{ minWidth: 120, fontFeatureSettings: '"liga" off, "clig" off', fontFamily: 'Roboto', fontSize: 12, fontStyle: 'normal', fontWeight: 400, lineHeight: '18px', letterSpacing: '0.08px' }}>Agent Runs:</Text>
                      <Text sx={{ fontFeatureSettings: '"liga" off, "clig" off', fontFamily: 'Roboto', fontSize: 12, fontStyle: 'normal', fontWeight: 400, lineHeight: '18px', letterSpacing: '0.08px' }}>{batchCount}</Text>
                    </Flex>
                    {/* Jobs run is not in the model; omit or populate if added later */}
                  </Flex>
                </Flex>
              </Flex>
            </Box>
          );
        },
        columns: {
          field: {
            title: '',
            layout: ExpandRowColumnLayout.Table,
            fields: {}
          }
        }
      }}
      appTableProps={{
        onToggleRowExpand: (event: any) => {
          // Prevent triggering row click when toggling caret
          event.stopPropagation();
        },
        sx: {
          '& tbody tr': { cursor: 'default !important' },
          '& thead th': { paddingTop: '10px !important', paddingBottom: '10px !important', height: '40px' }
        }
      }}
    />
  );
};

export default CrawlCampaignsEntityTable;


